/**
 * The `AbortController` + `nonce` + discriminated-state boilerplate every
 * settings/profile screen's data hook repeated on its own: fetch on mount and
 * on `reload()`, cancel a superseded/unmounted fetch, and land in `loading`,
 * `error` (with a trace id) or `ready` (with the data). Matches the shape
 * `useTaxonomy`/`useDocuments` already settled on — `reload` lives inside the
 * `error`/`ready` variants, not as a separate return value — so a caller
 * migrating to this hook keeps the same `state.status === 'ready' ?
 * state.reload : undefined` idiom.
 */
import { useCallback, useEffect, useRef, useState } from 'react';

import { ApiError } from '../api/errors';

export type LoadState<T> =
  | { status: 'loading' }
  | { status: 'error'; traceId: string | null; reload: () => void; refresh: () => void }
  | { status: 'ready'; data: T; reload: () => void; refresh: () => void };

function traceOf(error: unknown): string | null {
  if (error instanceof ApiError && error.problem !== null) {
    const problem = error.problem;
    const id = problem.correlation_id ?? problem.trace_id ?? problem.traceId;
    if (typeof id === 'string') return id;
  }
  return null;
}

/** True only for the AbortController-driven cancel of a superseded/unmounted load. */
function isAbort(error: unknown): boolean {
  return error instanceof DOMException && error.name === 'AbortError';
}

/**
 * Run `fetcher` on mount and whenever `reload()` is called, tracking it as a
 * `LoadState<T>`. `fetcher` must be stable (e.g. wrapped in `useCallback`) —
 * it is an effect dependency, so a fresh reference on every render would
 * refetch on every render.
 *
 * Two ways to fetch again, because a user asking and a timer asking want
 * different things:
 *
 * ``reload()``
 *     The user asked — after saving, or pressing Retry. Goes back through
 *     ``loading``, so the skeleton shows and the wait is visible.
 * ``refresh()``
 *     Something in the background asked — a poll noticing that work upstream may
 *     have produced new rows. Keeps the current data on screen while it fetches,
 *     and **keeps it on failure too**: replacing a working screen with an error
 *     because one background poll timed out is worse than showing data a few
 *     seconds stale. Pair it with `usePolling`.
 */
export function useLoadState<T>(fetcher: (signal: AbortSignal) => Promise<T>): LoadState<T> {
  const [state, setState] = useState<LoadState<T>>({ status: 'loading' });
  const [nonce, setNonce] = useState(0);
  // Set by `refresh` and read (then cleared) by the effect it triggers, so the
  // effect knows whether this run should be visible to the user.
  const quiet = useRef(false);

  const reload = useCallback(() => {
    quiet.current = false;
    setNonce((n) => n + 1);
  }, []);

  const refresh = useCallback(() => {
    quiet.current = true;
    setNonce((n) => n + 1);
  }, []);

  useEffect(() => {
    const controller = new AbortController();
    const signal = controller.signal;
    const background = quiet.current;
    quiet.current = false;
    if (!background) setState({ status: 'loading' });
    void (async () => {
      try {
        const data = await fetcher(signal);
        if (signal.aborted) return;
        setState({ status: 'ready', data, reload, refresh });
      } catch (error) {
        if (signal.aborted || isAbort(error)) return;
        // A background poll that fails leaves the last good data alone.
        if (background) return;
        setState({ status: 'error', traceId: traceOf(error), reload, refresh });
      }
    })();
    return () => {
      controller.abort();
    };
  }, [fetcher, nonce, reload, refresh]);

  return state;
}
